import type {
  ConfigurationOperation,
  OperationValidationError,
} from '@festina/configuration-general-types/configuration'
import { useMemo } from 'react'

import type { OperationWithSourceInfo } from '../types'
import type {
  ConfigurationItemsErrorMap,
  ValidationErrorWithLocation,
} from './types'

export const useConfigurationOperationErrorsMap = <
  C extends ConfigurationOperation,
  E extends OperationValidationError,
>({
  idFromError,
  operations,
  idFromOperation,
  errors,
}: {
  operations: OperationWithSourceInfo<C>[]
  idFromOperation: (item: OperationWithSourceInfo<C>) => string
  idFromError: (error: E) => string
  errors: ValidationErrorWithLocation<E>[]
}) =>
  useMemo(
    () =>
      errors.reduce<ConfigurationItemsErrorMap<E>>(
        (indexedErrors, validationError) => {
          const errorOperationKey = validationError.error.operationKey
          const indexedOperationForOperationKey = operations.find(
            (indexedOperation) =>
              indexedOperation.internalOperation.operationKey ===
              errorOperationKey,
          )

          const itemId =
            idFromError(validationError.error) ??
            (indexedOperationForOperationKey
              ? idFromOperation(indexedOperationForOperationKey)
              : '-')

          if (errorOperationKey) {
            if (!indexedErrors.get(itemId)) {
              indexedErrors.set(
                itemId,
                new Map<string, ValidationErrorWithLocation<E>[]>(),
              )
            }

            const configurationItemErrorsMap = indexedErrors.get(itemId)!

            if (!configurationItemErrorsMap.get(errorOperationKey)) {
              configurationItemErrorsMap.set(errorOperationKey, [])
            }

            const configurationItemOperationOperationErrorsArray =
              configurationItemErrorsMap.get(errorOperationKey)!

            configurationItemOperationOperationErrorsArray.push(validationError)
          }

          return indexedErrors
        },
        new Map<string, Map<string, ValidationErrorWithLocation<E>[]>>(),
      ),
    [errors, idFromError, idFromOperation, operations],
  )
